// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import handleAutoDeliveryModalData from "@guestsupply/App/Handlers/AutoDelivery/AutoDeliveryModalHandler";
import getAutoDelivery from "@guestsupply/App/Handlers/AutoDelivery/GetAutoDeliveryHandler";
import getEstimateTotals from "@guestsupply/App/Handlers/AutoDelivery/GetEstimateTotalsHandler";
import loadAutoDeliveryOrders from "@guestsupply/App/Handlers/AutoDelivery/loadAutoDeliveryOrders";
import setAutoDeliveryModalErrorMessage from "@guestsupply/App/Handlers/AutoDelivery/SetAutoDeliveryModalErrorMessage";
import setAutoDeliveryModalLoading from "@guestsupply/App/Handlers/AutoDelivery/SetAutoDeliveryModalIsDataLoading";
import setAutoDeliveryModalOpen from "@guestsupply/App/Handlers/AutoDelivery/SetAutoDeliveryModalOpen";
import skipNextAutoDeliveryOrder from "@guestsupply/App/Handlers/AutoDelivery/SkipNextAutoDeliveryOrderHandler";
import updateAutoDeliveryParameter from "@guestsupply/App/Handlers/AutoDelivery/UpdateAutoDeliveryParameterHandler";
import updateEmailPreference from "@guestsupply/App/Handlers/AutoDelivery/UpdateNotificationPreferenceHandler";
import { xCommonLoadingOverlay } from "@guestsupply/App/Handlers/XNG/Common";
import { GetAutoDeliveryCollectionApiParameter } from "@guestsupply/App/Services/AutoDelivery";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import { AutoDeliveryPageContext } from "@guestsupply/App/Store/Selectors/AutoDeliverySelectors";
import { getDataLayer } from "@guestsupply/App/Utilities/DataLayer";
import { LazyImage, LazyImageProps } from "@guestsupply/MobiusOverrides/LazyImage";
import Select, { SelectPresentationProps } from "@guestsupply/MobiusOverrides/Select/Select";
import { GOOGLE_EVENTS } from "@guestsupply/XDL/constants";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import addToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import ProductAddedToCartMessage from "@insite/content-library/Components/ProductAddedToCartMessage";
import Clickable from "@insite/mobius/Clickable";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const mapStateToProps = (state: CustomAppState) => {
    const isCartLoading = getCurrentCartState(state).isLoading;
    const { isUpdatingCart } = state.context;
    const {
        productSettings: { showAddToCartConfirmationDialog },
    } = getSettingsCollection(state);
    return {
        autoDeliveryParameter: state.xAutoDelivery.parameter,
        autoDeliveryOrdersType: state.xAutoDelivery.autoDeliveryOrdersType,
        isOrderNowButtonDisabled: isCartLoading || isUpdatingCart,
        showAddToCartConfirmationDialog,
        // xng-change: add google event
        xGoogleAnalytics: state.xGoogleAnalytics,
        currentBillTo: getCurrentBillToState(state).value,
    };
};

const mapDispatchToProps = {
    updateEmailPreference,
    updateAutoDeliveryParameter,
    skipNextAutoDeliveryOrder,
    loadAutoDeliveryOrders,
    addToCart: makeHandlerChainAwaitable(addToCart),
    xCommonLoadingOverlay,
    getEstimateTotals,
    setAutoDeliveryModalOpen,
    setAutoDeliveryModalErrorMessage,
    setAutoDeliveryModalLoading,
    getAutoDelivery,
    handleAutoDeliveryModalData,
};

export interface AutoDeliveryTableStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    autoDeliveryTableTitleWrapper?: InjectableCss;
    autoDeliveryTableTitleText?: TypographyProps;
    dataTable?: DataTableProps;
    dataTableHead?: DataTableHeadProps;
    dataTableHeaders?: DataTableHeaderProps;
    dataTableBody?: DataTableBodyProps;
    dataTableRow?: DataTableRowProps;
    dataTableCells?: DataTableCellBaseProps;
    dataCell?: DataTableCellProps;
    orderScheduleHeader?: DataTableHeaderProps;
    orderScheduleCells?: DataTableCellProps;
    productSubscHeader?: DataTableHeaderProps;
    productSubscCells?: DataTableCellProps;
    productWrapper?: InjectableCss;
    productImageClickable?: InjectableCss;
    productImage?: LazyImageProps;
    productPartNumber?: TypographyProps;
    productInfo?: InjectableCss;
    productShorDescription?: TypographyProps;
    orderPoHeader?: DataTableHeaderProps;
    orderPoCells?: DataTableCellProps;
    orderShipToHeader?: DataTableHeaderProps;
    orderShipToCells?: DataTableCellProps;
    orderEmailNotificationHeader?: DataTableHeaderProps;
    orderEmailNotificationCells?: DataTableCellProps;
    repeationStyle?: SelectPresentationProps;
    orderActionsHeader?: DataTableHeaderProps;
    orderActionsCells?: DataTableCellProps;
    actionsWrapper?: InjectableCss;
    skippedText?: TypographyProps;
    orderNowLink?: LinkPresentationProps;
    skipOrderLink?: LinkPresentationProps;
    editOrderLink?: LinkPresentationProps;
}
export const autoDeliveryTableStyles: AutoDeliveryTableStyles = {};
const styles = autoDeliveryTableStyles;
type Props = ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

const AutoDeliveryTable: React.FunctionComponent<Props> = ({
    autoDeliveryOrdersType,
    autoDeliveryParameter,
    isOrderNowButtonDisabled,
    showAddToCartConfirmationDialog,
    updateAutoDeliveryParameter,
    updateEmailPreference,
    skipNextAutoDeliveryOrder,
    addToCart,
    xCommonLoadingOverlay,
    setAutoDeliveryModalOpen,
    setAutoDeliveryModalErrorMessage,
    setAutoDeliveryModalLoading,
    handleAutoDeliveryModalData,
    loadAutoDeliveryOrders,
    // xng-change: add google event
    xGoogleAnalytics,
    currentBillTo,
}: Props) => {
    const [deliveredEvery, setDeliveredEvery] = useState("");
    const autoDelivery = React.useContext(AutoDeliveryPageContext);
    const toaster = React.useContext(ToasterContext);

    const handleAutoDeliveryPreferenceChange = (e: React.ChangeEvent<HTMLSelectElement>, productId: string) => {
        xCommonLoadingOverlay({ key: "add", value: "auto_delivery_page" });
        const v = e.currentTarget.value;
        updateEmailPreference({
            productId,
            notificationPreference: e.currentTarget.value,
            onError: errorMessage => {
                xCommonLoadingOverlay({ key: "clear", value: "auto_delivery_page" });
                toaster.addToast({
                    body: errorMessage,
                    messageType: "danger",
                });
            },
            onSuccess: result => {
                xCommonLoadingOverlay({ key: "clear", value: "auto_delivery_page" });

                if (autoDeliveryParameter) {
                    loadAutoDeliveryOrders(autoDeliveryParameter);
                }
            },
        });
    };
    const handleSkipNextAutoDeliveryClick = (productId: string) => {
        xCommonLoadingOverlay({ key: "add", value: "auto_delivery_page" });
        skipNextAutoDeliveryOrder({
            productId,
            onError: errorMessage => {
                xCommonLoadingOverlay({ key: "clear", value: "auto_delivery_page" });
                toaster.addToast({
                    body: errorMessage,
                    messageType: "danger",
                });
            },
            onSuccess: result => {
                xCommonLoadingOverlay({ key: "clear", value: "auto_delivery_table_skip_order" });
                updateAutoDeliveryParameter({
                    ...(autoDeliveryParameter as GetAutoDeliveryCollectionApiParameter),
                    skipOrder: productId,
                    // XNG-CHANGES : Adding uniqueId to call the auto delivery API on radio click
                    id: uniqueId(),
                });
                toaster.addToast({
                    body: "Next Auto-Delivery Date Skipped Successfully",
                    messageType: "success",
                });
            },
        });
    };

    const handleOrderNowClick = async (productId: string, unitOfMeasure: string, qty: number) => {
        xCommonLoadingOverlay({ key: "add", value: "auto_delivery_page" });
        (await addToCart({
            productId,
            qtyOrdered: qty,
            unitOfMeasure,

            onError: () => {
                xCommonLoadingOverlay({ key: "clear", value: "auto_delivery_page" });
                toaster.addToast({
                    body: "Failed To Add Item To Cart",
                    messageType: "danger",
                });
            },
        })) as CartLineModel;
        xCommonLoadingOverlay({ key: "clear", value: "auto_delivery_page" });
        if (showAddToCartConfirmationDialog) {
            toaster.addToast({
                body: <ProductAddedToCartMessage isQtyAdjusted={false} multipleProducts={false} />,
                messageType: "success",
            });
        }
    };
    const handlEditOrderClick = (productId: string) => {
        if (productId) {
            setAutoDeliveryModalLoading({ isDataLoading: true });
            setAutoDeliveryModalOpen({});
            xCommonLoadingOverlay({ key: "add", value: "auto_delivery_page" });
            handleAutoDeliveryModalData({
                productId,
                onError: (errorMessage: string) => {
                    setAutoDeliveryModalErrorMessage({
                        errorMessage: errorMessage ?? "An Error has Occured",
                    });
                    setAutoDeliveryModalLoading({ isDataLoading: false });
                    xCommonLoadingOverlay({ key: "clear", value: "auto_delivery_page" });
                },
                onSuccess: () => {
                    setAutoDeliveryModalLoading({ isDataLoading: false });
                    xCommonLoadingOverlay({ key: "clear", value: "auto_delivery_page" });
                    if (autoDeliveryParameter) {
                        loadAutoDeliveryOrders(autoDeliveryParameter);
                    }
                },
            });
        }
    };

    if (autoDelivery.isLoading) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner}></LoadingSpinner>
            </StyledWrapper>
        );
    }

    if (!autoDelivery.value) {
        return null;
    }

    if (autoDelivery.value.length === 0) {
        return (
            <StyledWrapper {...styles.noResultsContainer}>
                <Typography {...styles.noResultsText}>
                    {autoDeliveryOrdersType
                        ? siteMessage("Guest_AutoDelivery_Ongoing_Orders_Empty")
                        : siteMessage("Guest_AutoDelivery_Upcoming_Orders_Empty")}
                </Typography>
            </StyledWrapper>
        );
    }

    // xng-change: add google event
    const getDataLayerEvent = (action: string) => {
        if (xGoogleAnalytics.includeAutoDeliveryEvent) {
            const dataLayer = getDataLayer();

            const googleAnalyticsData = {
                event: GOOGLE_EVENTS.AUTO_DELIVERY,
                affiliation: currentBillTo?.companyName,
                brand_code: currentBillTo?.properties?.brand,
                exclusive: currentBillTo?.properties?.exclusive,
                group_descr: currentBillTo?.properties?.groupDescription,
                action,
            };

            dataLayer.push(googleAnalyticsData);
        }
    };

    // XNG-Changess
    return (
        <>
            <StyledWrapper {...styles.autoDeliveryTableTitleWrapper}>
                <Typography {...styles.autoDeliveryTableTitleText}>
                    {`My ${autoDeliveryOrdersType ? translate("Ongoing") : translate("Upcoming")} Orders`}
                </Typography>
            </StyledWrapper>
            <DataTable {...styles.dataTable}>
                <DataTableHead {...styles.dataTableHead}>
                    <DataTableHeader tight {...styles.orderScheduleHeader}>
                        {translate("Processing On")}
                    </DataTableHeader>
                    <DataTableHeader {...styles.productSubscHeader}>
                        {translate("Product Subscription ")}
                    </DataTableHeader>
                    <DataTableHeader {...styles.orderPoHeader}>{translate("PO #")}</DataTableHeader>
                    <DataTableHeader {...styles.orderShipToHeader}>{translate("Ship-to")}</DataTableHeader>
                    <DataTableHeader {...styles.orderEmailNotificationHeader}>
                        {translate("Email Notification")}
                    </DataTableHeader>
                    <DataTableHeader {...styles.orderActionsHeader}>{translate("Actions")}</DataTableHeader>
                </DataTableHead>
                <DataTableBody {...styles.dataTableBody}>
                    {autoDelivery.value &&
                        autoDelivery.value.map((order, index) => {
                            const notificationPreferences = [...order.notificationPreferences];
                            return (
                                <DataTableRow key={order.id}>
                                    <DataTableCell
                                        {...styles.orderScheduleCells}
                                        tight
                                        data-test-selector="AutoDeliveryTableCell_Schedule"
                                    >
                                        {order.scheduleDate}

                                        <Typography {...styles.skippedText}>
                                            {order.nextDeliverDateSkipped ? "Skipped" : null}
                                        </Typography>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.productSubscCells}
                                        data-test-selector="AutoDeliveryTableCell_ProductSubscription"
                                    >
                                        <StyledWrapper {...styles.productWrapper}>
                                            <StyledWrapper>
                                                <Clickable
                                                    {...styles.productImageClickable}
                                                    href={order.productDetailUrl}
                                                    aria-label="product image clickable"
                                                >
                                                    <LazyImage
                                                        {...styles.productImage}
                                                        height="100"
                                                        width="100"
                                                        src={order.smallImagePath}
                                                        altText={order.shortDescription}
                                                    />
                                                </Clickable>
                                            </StyledWrapper>
                                            <StyledWrapper {...styles.productInfo}>
                                                {order.productErpNumber && (
                                                    <Typography
                                                        {...styles.productPartNumber}
                                                    >{`Part #: ${order.productErpNumber}`}</Typography>
                                                )}
                                                {order.shortDescription && (
                                                    <Typography {...styles.productShorDescription}>
                                                        {order.shortDescription}
                                                    </Typography>
                                                )}
                                            </StyledWrapper>
                                        </StyledWrapper>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderPoCells}
                                        data-test-selector="AutoDeliveryTableCell_PO"
                                    >
                                        {order.purchaseOrderNo}
                                    </DataTableCell>

                                    <DataTableCell
                                        {...styles.orderShipToCells}
                                        data-test-selector="AutoDeliveryTableCell_ShipTo"
                                    >
                                        {order.shipToLabel}
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderEmailNotificationCells}
                                        data-test-selector="AutoDeliveryTableCell_EmailNotification"
                                    >
                                        <Select
                                            label=""
                                            placeholder="Select"
                                            onChange={event => {
                                                handleAutoDeliveryPreferenceChange(event, order.productId);
                                                setDeliveredEvery(event.target?.value);
                                            }}
                                            value={order.notificationPreference || deliveredEvery}
                                            {...styles.repeationStyle}
                                        >
                                            <option value="">{translate("Select Option")}</option>
                                            {notificationPreferences
                                                .sort((a, b) => a.days - b.days)
                                                .map((item, id) => (
                                                    <option key={`${item.displayName}_${id}`} value={item.displayName}>
                                                        {item.displayName}
                                                    </option>
                                                ))}
                                        </Select>
                                    </DataTableCell>
                                    <DataTableCell
                                        {...styles.orderActionsCells}
                                        data-test-selector="AutoDeliveryTableCell_orderActions"
                                    >
                                        <StyledWrapper {...styles.actionsWrapper}>
                                            <Link
                                                {...styles.orderNowLink}
                                                disabled={!order.canAddToCart || isOrderNowButtonDisabled}
                                                onClick={() => {
                                                    handleOrderNowClick(order.productId, order.uom, order.qty);
                                                    getDataLayerEvent("Order Now");
                                                }}
                                            >
                                                {translate("Order Now")}
                                            </Link>

                                            <Link
                                                {...styles.skipOrderLink}
                                                disabled={order.nextDeliverDateSkipped}
                                                onClick={() => {
                                                    handleSkipNextAutoDeliveryClick(order.productId);
                                                    getDataLayerEvent("Skip Order");
                                                }}
                                            >
                                                {translate("Skip Order")}
                                            </Link>
                                            <Link
                                                {...styles.editOrderLink}
                                                onClick={() => {
                                                    handlEditOrderClick(order.productId);
                                                    getDataLayerEvent("Edit Order Options");
                                                }}
                                            >
                                                {translate("Edit Order Options")}
                                            </Link>
                                        </StyledWrapper>
                                    </DataTableCell>
                                </DataTableRow>
                            );
                        })}
                </DataTableBody>
            </DataTable>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(AutoDeliveryTable),
    definition: {
        group: "Auto Delivery" as any,
        allowedContexts: ["AutoDeliveryPage"],
    },
};
export default widgetModule;
